import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  CalendarCheck, Clock, MapPin, Star, FileText, Download,
  User, Phone, Mail, Home, Key, Edit3, Save, X, Pause,
  Play, Trash2, RefreshCw, ChevronDown, ChevronUp, Plus, Copy
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SEOHead } from "@/components/SEOHead";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  en_attente: { label: "En attente", color: "bg-amber-100 text-amber-700" },
  confirme: { label: "Confirmée", color: "bg-emerald-100 text-emerald-700" },
  termine: { label: "Terminée", color: "bg-blue-100 text-blue-700" },
  annule: { label: "Annulée", color: "bg-red-100 text-red-700" },
};

const aboStatusLabels = {
  actif: { label: "Actif", color: "bg-emerald-100 text-emerald-700" },
  pause: { label: "En pause", color: "bg-amber-100 text-amber-700" },
  annule: { label: "Annulé", color: "bg-red-100 text-red-700" },
};

const prestationLabels = {
  menage: "Ménage",
  vitres: "Nettoyage vitres",
  remise_etat: "Remise en état",
};

const formatDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
};

const formatDateShort = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" });
};

// ========== NEXT INTERVENTION BANNER ==========
const NextInterventionBanner = ({ reservations, abonnements }) => {
  const upcoming = reservations.filter(r => r.status !== "annule");
  const nextRes = upcoming[0];
  const activeAbo = abonnements.find(a => a.statut === "actif");

  if (!nextRes && !activeAbo) {
    return (
      <div className="bg-gray-50 border-2 border-dashed border-gray-200 rounded-2xl p-8 text-center" data-testid="no-intervention-banner">
        <CalendarCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <h3 className="text-lg font-semibold text-gray-500 mb-1">Aucune intervention prévue</h3>
        <p className="text-gray-400 text-sm mb-4">Réservez dès maintenant votre prochaine prestation</p>
      </div>
    );
  }

  const display = nextRes || {};
  const isAbo = !nextRes && activeAbo;
  const date = isAbo ? activeAbo.prochaine_intervention : display.date_souhaitee;
  const heure = isAbo ? activeAbo.heure : display.heure_souhaitee;
  const type = isAbo ? activeAbo.type_prestation : display.type_prestation;
  const prix = isAbo ? activeAbo.prix_apres_credit : display.prix_apres_credit;
  const status = isAbo ? "Abonnement actif" : statusLabels[display.status]?.label;

  return (
    <div className="bg-gradient-to-r from-[#003264] to-[#004a96] rounded-2xl p-6 text-white" data-testid="next-intervention-banner">
      <div className="flex items-center gap-2 mb-1 text-white/70 text-xs font-medium uppercase tracking-wider">
        <CalendarCheck className="w-4 h-4" />
        Prochaine intervention
      </div>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-3">
        <div className="space-y-1">
          <h3 className="text-xl font-bold">{formatDate(date)}</h3>
          <div className="flex items-center gap-4 text-white/80 text-sm">
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {heure}</span>
            <span>{prestationLabels[type] || type}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {prix != null && (
            <div className="bg-white/10 rounded-xl px-4 py-2 text-center">
              <p className="text-xs text-white/60">Après crédit d'impôt</p>
              <p className="text-xl font-bold">{prix}€</p>
            </div>
          )}
          <span className="px-3 py-1 bg-white/20 rounded-full text-xs font-medium">{status}</span>
        </div>
      </div>
    </div>
  );
};

// ========== RATING COMPONENT ==========
const RatingStars = ({ reservationId, currentRating, currentComment, token, onRated }) => {
  const [rating, setRating] = useState(currentRating || 0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState(currentComment || "");
  const [editing, setEditing] = useState(!currentRating);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) return toast.error("Veuillez sélectionner une note");
    setSubmitting(true);
    try {
      await axios.post(`${API}/reservation/${reservationId}/noter`, { rating, comment_client: comment }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Merci pour votre avis !");
      setEditing(false);
      if (onRated) onRated();
    } catch {
      toast.error("Erreur lors de l'envoi de la note");
    } finally {
      setSubmitting(false);
    }
  };

  if (!editing && currentRating) {
    return (
      <div className="flex items-center gap-2">
        {[1,2,3,4,5].map(i => (
          <Star key={i} className={`w-4 h-4 ${i <= currentRating ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
        ))}
        {currentComment && <span className="text-xs text-gray-500 ml-2">"{currentComment}"</span>}
      </div>
    );
  }

  return (
    <div className="space-y-2 bg-amber-50 rounded-xl p-3" data-testid="rating-form">
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map(i => (
          <button key={i} onClick={() => setRating(i)} onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}>
            <Star className={`w-5 h-5 transition-colors ${i <= (hover || rating) ? "fill-amber-400 text-amber-400" : "text-gray-300"}`} />
          </button>
        ))}
        <span className="text-xs text-gray-500 ml-2">{rating}/5</span>
      </div>
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Votre commentaire (optionnel)"
        className="w-full text-sm border border-amber-200 rounded-lg p-2 resize-none h-16 focus:outline-none focus:ring-2 focus:ring-amber-300"
        data-testid="rating-comment"
      />
      <Button size="sm" onClick={handleSubmit} disabled={submitting || rating === 0} className="bg-amber-500 hover:bg-amber-600 text-white text-xs" data-testid="rating-submit">
        {submitting ? "Envoi..." : "Envoyer mon avis"}
      </Button>
    </div>
  );
};

// ========== RESERVATION CARD ==========
const ReservationCard = ({ res, isPast, token, onUpdate }) => {
  const [showModify, setShowModify] = useState(false);
  const [newDate, setNewDate] = useState(res.date_souhaitee || "");
  const [newHeure, setNewHeure] = useState(res.heure_souhaitee || "");
  const [loading, setLoading] = useState(false);

  const s = statusLabels[res.status] || statusLabels.en_attente;

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cette réservation ?")) return;
    try {
      await axios.patch(`${API}/reservation/${res.id}/annuler`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Réservation annulée");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const handleModify = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/reservation/${res.id}/modifier`, { date_souhaitee: newDate, heure_souhaitee: newHeure }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Réservation modifiée (en attente de re-validation)");
      setShowModify(false);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`reservation-card-${res.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-sm font-semibold text-[#003264]">{prestationLabels[res.type_prestation] || res.type_prestation}</span>
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
        </div>
        {res.prix_apres_credit != null && (
          <span className="text-sm font-bold text-emerald-600">{res.prix_apres_credit}€</span>
        )}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-3">
        <span className="flex items-center gap-1"><CalendarCheck className="w-3 h-3" /> {formatDateShort(res.date_souhaitee)}</span>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {res.heure_souhaitee}</span>
        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {res.adresse}</span>
      </div>

      {/* Admin comment if any */}
      {res.comment_admin && (
        <div className="bg-blue-50 text-blue-700 text-xs px-3 py-2 rounded-lg mb-3">
          <strong>Note IKS :</strong> {res.comment_admin}
        </div>
      )}

      {/* Actions for non-cancelled, non-past */}
      {!isPast && res.status !== "annule" && (
        <div className="flex gap-2 mt-2">
          <Button size="sm" variant="outline" className="text-xs" onClick={() => setShowModify(!showModify)} data-testid={`modify-btn-${res.id}`}>
            <Edit3 className="w-3 h-3 mr-1" /> Modifier
          </Button>
          <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancel} data-testid={`cancel-btn-${res.id}`}>
            <X className="w-3 h-3 mr-1" /> Annuler
          </Button>
        </div>
      )}

      {/* Modify form */}
      {showModify && (
        <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">Nouvelle date</Label>
              <Input type="date" value={newDate} onChange={e => setNewDate(e.target.value)} className="h-9 text-sm" data-testid="modify-date" />
            </div>
            <div>
              <Label className="text-xs">Nouvelle heure</Label>
              <Input type="time" value={newHeure} onChange={e => setNewHeure(e.target.value)} className="h-9 text-sm" data-testid="modify-time" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleModify} disabled={loading} className="bg-[#003264] text-white text-xs" data-testid="modify-confirm">
              <Save className="w-3 h-3 mr-1" /> Confirmer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setShowModify(false)} className="text-xs">Annuler</Button>
          </div>
        </div>
      )}

      {/* Rating for past completed */}
      {isPast && res.status !== "annule" && (
        <div className="mt-3 pt-3 border-t border-gray-100">
          <p className="text-xs font-medium text-gray-500 mb-2">Votre avis :</p>
          <RatingStars
            reservationId={res.id}
            currentRating={res.rating}
            currentComment={res.comment_client}
            token={token}
            onRated={() => onUpdate()}
          />
        </div>
      )}
    </div>
  );
};

// ========== ABONNEMENT CARD ==========
const AbonnementCard = ({ abo, token, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const s = aboStatusLabels[abo.statut] || aboStatusLabels.actif;

  const freqLabels = {
    quotidien: "Quotidien", hebdomadaire: "Hebdomadaire",
    bi_mensuel: "Bi-mensuel", mensuel: "Mensuel"
  };

  const handlePause = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/abonnement/${abo.id}/pause`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abonnement mis en pause");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleResume = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/abonnement/${abo.id}/reprendre`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abonnement repris !");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  const handleCancel = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir annuler cet abonnement ?")) return;
    setLoading(true);
    try {
      await axios.patch(`${API}/abonnement/${abo.id}/annuler-client`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Abonnement annulé");
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-md transition-shadow" data-testid={`abonnement-card-${abo.id}`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <span className="text-sm font-semibold text-[#003264]">{prestationLabels[abo.type_prestation] || abo.type_prestation}</span>
          <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-medium ${s.color}`}>{s.label}</span>
        </div>
        <div className="text-right">
          <span className="text-sm font-bold text-emerald-600">{abo.prix_apres_credit}€</span>
          <p className="text-[10px] text-gray-400">après crédit d'impôt</p>
        </div>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 mb-2">
        <span className="font-medium text-[#003264]">{freqLabels[abo.frequence] || abo.frequence}</span>
        {abo.jour_semaine && <span>Le {abo.jour_semaine}</span>}
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {abo.heure}</span>
      </div>
      {abo.prochaine_intervention && abo.statut === "actif" && (
        <p className="text-xs text-emerald-600 font-medium mb-3">Prochaine : {formatDateShort(abo.prochaine_intervention)}</p>
      )}
      {abo.statut !== "annule" && (
        <div className="flex gap-2 mt-2">
          {abo.statut === "actif" && (
            <Button size="sm" variant="outline" className="text-xs text-amber-600 border-amber-200 hover:bg-amber-50" onClick={handlePause} disabled={loading} data-testid={`pause-abo-${abo.id}`}>
              <Pause className="w-3 h-3 mr-1" /> Pause
            </Button>
          )}
          {abo.statut === "pause" && (
            <Button size="sm" variant="outline" className="text-xs text-emerald-600 border-emerald-200 hover:bg-emerald-50" onClick={handleResume} disabled={loading} data-testid={`resume-abo-${abo.id}`}>
              <Play className="w-3 h-3 mr-1" /> Reprendre
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-xs text-red-600 border-red-200 hover:bg-red-50" onClick={handleCancel} disabled={loading} data-testid={`cancel-abo-${abo.id}`}>
            <Trash2 className="w-3 h-3 mr-1" /> Annuler
          </Button>
        </div>
      )}
    </div>
  );
};

// ========== PROFILE EDITOR ==========
const ProfileEditor = ({ profile, token, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...profile });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/mon-profil`, form, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profil mis à jour !");
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="profile-editor">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#003264] flex items-center gap-2">
          <User className="w-5 h-5" /> Mon profil
        </h3>
        {!editing ? (
          <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="edit-profile-btn">
            <Edit3 className="w-3 h-3 mr-1" /> Modifier
          </Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#003264] text-white text-xs" data-testid="save-profile-btn">
              <Save className="w-3 h-3 mr-1" /> Enregistrer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setForm({ ...profile }); }} className="text-xs">Annuler</Button>
          </div>
        )}
      </div>
      {editing ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-gray-500">Nom</Label>
            <Input value={form.nom} onChange={e => setForm(p => ({...p, nom: e.target.value}))} className="h-10" data-testid="profile-nom" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Prénom</Label>
            <Input value={form.prenom} onChange={e => setForm(p => ({...p, prenom: e.target.value}))} className="h-10" data-testid="profile-prenom" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Téléphone</Label>
            <Input value={form.telephone} onChange={e => setForm(p => ({...p, telephone: e.target.value}))} className="h-10" data-testid="profile-telephone" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Adresse</Label>
            <Input value={form.adresse} onChange={e => setForm(p => ({...p, adresse: e.target.value}))} className="h-10" data-testid="profile-adresse" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Code postal</Label>
            <Input value={form.code_postal} onChange={e => setForm(p => ({...p, code_postal: e.target.value}))} className="h-10" data-testid="profile-cp" />
          </div>
          <div>
            <Label className="text-xs text-gray-500">Ville</Label>
            <Input value={form.ville} onChange={e => setForm(p => ({...p, ville: e.target.value}))} className="h-10" data-testid="profile-ville" />
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs text-gray-500 flex items-center gap-1"><Key className="w-3 h-3" /> Instructions d'accès au logement</Label>
            <textarea
              value={form.acces_logement || ""}
              onChange={e => setForm(p => ({...p, acces_logement: e.target.value}))}
              className="w-full mt-1 text-sm border border-gray-200 rounded-lg p-3 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#003264]/20"
              placeholder="Code d'entrée, étage, digicode, clé sous le paillasson..."
              data-testid="profile-access-codes"
            />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex items-center gap-2 text-sm"><User className="w-4 h-4 text-gray-400" /> {profile.prenom} {profile.nom}</div>
          <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /> {profile.email}</div>
          <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /> {profile.telephone || "—"}</div>
          <div className="flex items-center gap-2 text-sm"><Home className="w-4 h-4 text-gray-400" /> {profile.adresse || "—"}, {profile.code_postal} {profile.ville}</div>
          {profile.acces_logement && (
            <div className="sm:col-span-2 flex items-start gap-2 text-sm bg-amber-50 p-3 rounded-lg">
              <Key className="w-4 h-4 text-amber-500 mt-0.5" />
              <span>{profile.acces_logement}</span>
            </div>
          )}
          <div className="sm:col-span-2 flex items-center gap-2 text-sm">
            <Copy className="w-4 h-4 text-gray-400" />
            <span className="text-gray-500">Code parrainage :</span>
            <span className="font-mono font-bold text-[#003264]">{profile.code_parrainage}</span>
            <button onClick={() => { navigator.clipboard.writeText(profile.code_parrainage); toast.success("Code copié !"); }} className="text-xs text-[#003264] hover:underline">Copier</button>
          </div>
        </div>
      )}
    </div>
  );
};

// ========== MAIN PAGE ==========
export const EspaceClientPage = () => {
  const { user, loading: authLoading, getToken } = useAuth();
  const navigate = useNavigate();
  const token = getToken();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("apercu");

  const fetchData = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/mon-espace`, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch {
      toast.error("Erreur de chargement");
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) navigate("/connexion", { state: { from: "/mon-espace" } });
  }, [authLoading, user, navigate]);

  if (authLoading || loading || !data) {
    return (
      <div className="pt-16 min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#003264] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const tabs = [
    { id: "apercu", label: "Aper\u00e7u", count: null },
    { id: "reservations", label: "R\u00e9servations", count: data.upcoming_reservations.length + data.past_reservations.length },
    { id: "abonnements", label: "Abonnements", count: data.abonnements.length },
    { id: "devis", label: "Devis", count: (data.devis || []).length },
    { id: "factures", label: "Factures", count: data.factures.length },
    { id: "profil", label: "Profil", count: null },
  ];

  return (
    <div className="pt-16 min-h-screen bg-gray-50" data-testid="espace-client-page">
      <SEOHead title="Mon Espace Client - IKS" description="Gérez vos réservations, abonnements et factures" path="/mon-espace" />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003264]" data-testid="espace-client-title">
              Bonjour {data.profile.prenom} !
            </h1>
            <p className="text-gray-500 text-sm mt-1">Gérez vos prestations et votre compte</p>
          </div>
          <Button onClick={() => navigate("/particuliers")} className="bg-[#003264] hover:bg-[#002244] text-white" data-testid="book-again-btn">
            <Plus className="w-4 h-4 mr-2" /> Réserver une prestation
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 border border-gray-200 mb-6 overflow-x-auto" data-testid="espace-client-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? "bg-[#003264] text-white" : "text-gray-500 hover:bg-gray-100"
              }`}
              data-testid={`tab-${tab.id}`}
            >
              {tab.label}
              {tab.count != null && tab.count > 0 && (
                <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                  activeTab === tab.id ? "bg-white/20" : "bg-gray-200 text-gray-600"
                }`}>{tab.count}</span>
              )}
            </button>
          ))}
        </div>

        {/* TAB: APERCU */}
        {activeTab === "apercu" && (
          <div className="space-y-6">
            <NextInterventionBanner
              reservations={data.upcoming_reservations}
              abonnements={data.abonnements}
            />

            {/* Quick stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-[#003264]">{data.upcoming_reservations.length}</p>
                <p className="text-xs text-gray-500">À venir</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{data.abonnements.filter(a => a.statut === "actif").length}</p>
                <p className="text-xs text-gray-500">Abonnements actifs</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-blue-600">{data.past_reservations.filter(r => r.status !== "annule").length}</p>
                <p className="text-xs text-gray-500">Interventions passées</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-amber-500">{data.factures.length}</p>
                <p className="text-xs text-gray-500">Factures</p>
              </div>
              <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
                <p className="text-2xl font-bold text-orange-500">{(data.devis || []).length}</p>
                <p className="text-xs text-gray-500">Devis</p>
              </div>
            </div>

            {/* Upcoming reservations preview */}
            {data.upcoming_reservations.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Prochaines réservations</h3>
                <div className="space-y-3">
                  {data.upcoming_reservations.slice(0, 3).map(res => (
                    <ReservationCard key={res.id} res={res} isPast={false} token={token} onUpdate={fetchData} />
                  ))}
                </div>
                {data.upcoming_reservations.length > 3 && (
                  <button onClick={() => setActiveTab("reservations")} className="mt-2 text-sm text-[#003264] hover:underline font-medium">
                    Voir toutes les réservations →
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* TAB: RESERVATIONS */}
        {activeTab === "reservations" && (
          <div className="space-y-6">
            {data.upcoming_reservations.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-[#003264] mb-3 flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4" /> À venir ({data.upcoming_reservations.length})
                </h3>
                <div className="space-y-3">
                  {data.upcoming_reservations.map(res => (
                    <ReservationCard key={res.id} res={res} isPast={false} token={token} onUpdate={fetchData} />
                  ))}
                </div>
              </div>
            )}
            {data.past_reservations.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Historique ({data.past_reservations.length})
                </h3>
                <div className="space-y-3">
                  {data.past_reservations.map(res => (
                    <ReservationCard key={res.id} res={res} isPast={true} token={token} onUpdate={fetchData} />
                  ))}
                </div>
              </div>
            )}
            {data.upcoming_reservations.length === 0 && data.past_reservations.length === 0 && (
              <div className="text-center py-12">
                <CalendarCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucune réservation pour le moment</p>
                <Button onClick={() => navigate("/particuliers")} className="mt-4 bg-[#003264] text-white" data-testid="book-from-reservations">
                  <Plus className="w-4 h-4 mr-2" /> Réserver maintenant
                </Button>
              </div>
            )}
          </div>
        )}

        {/* TAB: ABONNEMENTS */}
        {activeTab === "abonnements" && (
          <div className="space-y-3">
            {data.abonnements.length > 0 ? data.abonnements.map(abo => (
              <AbonnementCard key={abo.id} abo={abo} token={token} onUpdate={fetchData} />
            )) : (
              <div className="text-center py-12">
                <RefreshCw className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun abonnement actif</p>
                <Button onClick={() => navigate("/particuliers")} className="mt-4 bg-[#003264] text-white" data-testid="subscribe-btn">
                  <Plus className="w-4 h-4 mr-2" /> Souscrire un abonnement
                </Button>
              </div>
            )}
          </div>
        )}

        {/* TAB: DEVIS */}
        {activeTab === "devis" && (
          <div className="space-y-3">
            {(data.devis || []).length > 0 ? data.devis.map(d => (
              <div key={d.id} className="bg-white rounded-xl border border-amber-200 p-4 flex items-center gap-4" data-testid={`devis-card-${d.id}`}>
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#003264]">{d.description || "Devis"}</p>
                  <p className="text-xs text-gray-500">{formatDateShort(d.created_at)}</p>
                  <p className="text-xs text-gray-400 truncate">{d.filename}</p>
                </div>
                <a
                  href={`${API}/devis/download/${d.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-amber-500 text-white rounded-lg text-xs font-medium hover:bg-amber-600 transition"
                  data-testid={`download-devis-${d.id}`}
                >
                  <Download className="w-3 h-3" /> PDF
                </a>
              </div>
            )) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucun devis disponible</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: FACTURES */}
        {activeTab === "factures" && (
          <div className="space-y-3">
            {data.factures.length > 0 ? data.factures.map(f => (
              <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4" data-testid={`facture-card-${f.id}`}>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <FileText className="w-5 h-5 text-red-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-[#003264]">{f.prestation_type || "Facture"}</p>
                  <p className="text-xs text-gray-500">{f.prestation_date ? formatDateShort(f.prestation_date) : formatDateShort(f.created_at)}</p>
                  {f.description && <p className="text-xs text-gray-400 truncate">{f.description}</p>}
                </div>
                <a
                  href={`${API}/facture/download/${f.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 px-3 py-2 bg-[#003264] text-white rounded-lg text-xs font-medium hover:bg-[#002244] transition"
                  data-testid={`download-facture-${f.id}`}
                >
                  <Download className="w-3 h-3" /> PDF
                </a>
              </div>
            )) : (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">Aucune facture disponible</p>
              </div>
            )}
          </div>
        )}

        {/* TAB: PROFIL */}
        {activeTab === "profil" && (
          <ProfileEditor profile={data.profile} token={token} onUpdate={fetchData} />
        )}
      </div>
    </div>
  );
};
